import { Button, Checkbox, Layout, message, Tooltip } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../store/hooks/hooks';
import { userSubMenu } from '../../../utils/functions';
import Navbar from '../../Organisms/Navbar/Navbar';
import './TaskMapTemplate.less';
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import MapButtons from '../../Organisms/MapButtons/MapButtons';
import {
  addPoint,
  addPointToLine,
  cancelAddingGeometry,
  closeMapTutorial,
  deletePointOfLineSelected,
  editExistingLine, resetMap,
  saveCurrentShape,
  selectAddingGeometry,
  selectAddingGeometryType,
  selectEditingGeometry,
  selectGeometrySelected,
  selectLineId,
  selectLines, selectMapError,
  selectMapTutorial,
  selectPoints,
  selectPolygonId,
  selectSyncPolygonId,
  selectSyncLineId,
  selectPolygons,
  selectSyncPoints,
  selectSyncLines,
  selectSyncPolygons, unselectGeometry,
} from '../../../store/slice/mapSlice';
import { ReactComponent as InfoCircle } from '../../../res/svg/info-circle.svg';
import TaskMapView from '../../Organisms/TaskMapView/TaskMapView';
import Map, { Layer, MapLayerMouseEvent, Source, useMap } from 'react-map-gl';
import {
  Geometry,
  LatLong,
  Line, MapLayer,
  Point,
  Polygon,
} from '../../../models/Geometries';
import MapPopup from '../../Atoms/MapPopup/MapPopup';
import MapPointMarker from '../../Atoms/MapPointMarker/MapPointMarker';
import MapCurrentLine from '../../Atoms/MapCurrentLine/MapCurrentLine';
import MapLineMarker from '../../Atoms/MapLineMarker/MapLineMarker';
import { featureCollection, lineString } from '@turf/helpers';
import bbox from '@turf/bbox';
import MapBottomLineControl from '../../Atoms/MapBottomLineControl/MapBottomLineControl';
import MapPolygonMarker from '../../Atoms/MapPolygonMarker/MapPolygonMarker';
import {
  addMultiplesFeaturesPoint, closeMultipleSelectionTutorial, closeAddGeometriesTutorial,
  getAllLayers, resetLayers, selectFeature,
  selectFeatureSelected,
  selectLayers,
  selectMapLayersError, selectTaskFeatures,
  showLayerAttributes, unselectFeature, unselectPreselectedFeature,
} from '../../../store/slice/mapLayersSlice';
import { getLayerUrl } from '../../../store/services/map/map.service';
import { Feature, featureToGeometry } from '../../../models/Geoserver';
import MapCMFeaturePopup from '../../Atoms/MapCMFeaturePopup/MapCMFeaturePopup';
import MapOrderingLayer from '../../Atoms/MapOrderingLayer/MapOrderingLayer';
import MapCMGeometrySelected from '../../Atoms/MapCMGeometrySelected/MapCMGeometrySelected';
import MapCMGeometries from '../../Atoms/MapCMGeometries/MapCMGeometries';
import MapCMGeometryRemoved from '../../Atoms/MapCMGeometryRemoved/MapCMGeometryRemoved';
import { clearCurrentTask, getTask } from '../../../store/slice/taskSlice';
import { addAOIPoint, closeAOITutorial, saveAOI, setAddingAOI, setAOISelected } from '../../../store/slice/mapAOISlice';
import MapAOI from '../../Atoms/MapAOI/MapAOI';
import { throttle } from '../../../utils/throttle';
import MapUserLocation from '../../Atoms/MapUserLocation/MapUserLocation';
import SyncTaskPopup from '../../Atoms/SyncTaskPopup/SyncTaskPopup';
import MapMultipleSelection from '../../Atoms/MapMultipleSelection/MapMultipleSelection';
import MapCMMultipleGeometriesSelected
  from '../../Atoms/MapCMMultipleGeometriesSelected/MapCMMultipleGeometriesSelected';
import MapAddMultipleGeometriesButton from '../../Atoms/MapAddMultipleGeometriesButton/MapAddMultipleGeometriesButton';
import { centerMapOnGeometries, degreesToDegreeMinuteSecond } from '../../../utils/GeometryUtils';
import MapVideoTutorial from '../../Atoms/MapVideoTutorial/MapVideoTutorial';

//Bounds para centrar en Rio Negro
const INITIAL_BOUNDS = [
  {lng: -72.68555303277898, lat: -42.95426964314728},
  {lng: -62.78362915605197, lat: -37.184980564174644},
];

interface Props {
  editing: boolean;
}

//bounds por defecto, que permite a una capa mostrarse en todo el mapa
const defaultBounds = [-180,-90,180,90];

function TaskMapTemplate({editing}: Props) {
  let navigate = useNavigate();
  const { map } = useMap();
  const dispatch = useAppDispatch();
  const [mousePosition, setMousePosition] = useState<LatLong | undefined>();
  const mapTutorial = useAppSelector(selectMapTutorial);
  const aoiTutorial = useAppSelector(state => state.mapAOI.aoiTutorial);
  const multipleSelectionTutorial = useAppSelector(state => state.mapLayers.multipleSelectionTutorial);
  const addGeometriesTutorial = useAppSelector(state => state.mapLayers.addGeometriesTutorial);
  const addingGeometry = useAppSelector(selectAddingGeometry);
  const addingGeometryType = useAppSelector(selectAddingGeometryType);
  const mapLayersError = useAppSelector(selectMapLayersError);
  const mapError = useAppSelector(selectMapError)
  const mapAoiError = useAppSelector(state => state.mapAOI.error)
  const addingAOI = useAppSelector(state => state.mapAOI.addingAOI);
  const addingMultipleFeatures = useAppSelector(state => state.mapLayers.addingMultipleFeatures);
  const addingMultipleFeaturesSecondPoint = useAppSelector(state => state.mapLayers.secondPoint);
  const layers: MapLayer[] = useAppSelector(selectLayers);
  const aoi = useAppSelector(state => state.mapAOI.aoi);
  const aoiVisible = useAppSelector(state => state.mapAOI.aoiVisible);
  const userLocation = useAppSelector(state => state.map.userLocation);
  const canEditGeometries = useAppSelector(state => state.task.canEditGeometries);
  const geometrySelected: Geometry | undefined = useAppSelector(
    selectGeometrySelected,
  );
  const editingGeometry: Geometry | undefined = useAppSelector(
    selectEditingGeometry,
  );
  const featureSelected: Feature | undefined = useAppSelector(
    selectFeatureSelected,
  );
  const taskFeatures: Feature[] = useAppSelector(
    selectTaskFeatures,
  );
  const points: Point[] = useAppSelector(selectPoints);
  const lines: Line[] = useAppSelector(selectLines);
  const polygons: Polygon[] = useAppSelector(selectPolygons);
  //Se maneja arrays distintos para las geometrias sincronizadas
  const syncPoints: Point[] = useAppSelector(selectSyncPoints);
  const syncLines: Line[] = useAppSelector(selectSyncLines);
  const syncPolygons: Polygon[] = useAppSelector(selectSyncPolygons);
  const allGeometries = useAppSelector(state => state.map.allGeometries);
  const [dontShow, setDontShow] = useState<boolean>(false);
  const [dragging, setDragging] = useState<boolean>(false);
    useState<boolean>(false);
  const [hoveringId, setHoveringId] = useState<number>(0);
  const [layerIds, setLayerIds] = useState<string[]>([]);
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const itemsCallback = (item: string) => {
    userSubMenu(item, navigate, dispatch);
  };
  let urlParams = useParams();

  useLayoutEffect(() => {
    dispatch(resetMap())
    dispatch(resetLayers())
    dispatch(setAddingAOI(false))
    if(urlParams.id) {
      dispatch(getTask(parseInt(urlParams.id)));
    } else {
      dispatch(clearCurrentTask())
    }
  }, []);

  const centerOnPoint = useCallback(
    (point: Point) => {
      map?.flyTo({
        center: [point.longitud, point.latitud],
        duration: 500,
      });
    },
    [map],
  );
  useEffect(() => {
    dispatch(getAllLayers())
  }, [dispatch]);
  useEffect(() => {
    if(mapLayersError) {
      message.error({ content: mapLayersError });
    }
  }, [mapLayersError])
  useEffect(() => {
    if(mapError) {
      message.error({ content: mapError });
    }
  }, [mapError])
  useEffect(() => {
    if(mapAoiError) {
      message.error({ content: mapAoiError });
    }
  }, [mapAoiError])

  useEffect(() => {
    if(userLocation){
      map?.flyTo({
        center: [userLocation.longitud, userLocation.latitud],
        duration: 500,
        zoom: 14
      });
    }
  }, [userLocation])

  useEffect(() => {
    const handleCopy = (e: any) => {
      if (mousePosition && e.ctrlKey && e.shiftKey && e.key === 'C') {
        e.preventDefault();
        navigator.clipboard.writeText(`${degreesToDegreeMinuteSecond(mousePosition.latitud, true)} / ${degreesToDegreeMinuteSecond(mousePosition.longitud, false)}`);
      }
    };

    document.addEventListener('keydown', handleCopy);

    return () => {
      document.removeEventListener('keydown', handleCopy);
    };
  }, [mousePosition]);


  useEffect(() => {
    if (!editingGeometry) return;
    const feature = lineString(
      (editingGeometry as Line).puntos.map((p) => [p.longitud, p.latitud]),
    );
    const box = bbox(feature);
    const editingPadding = { top: 32, left: 32, bottom: 200, right: 32 };
    map?.fitBounds(
      [
        [box[0], box[1]],
        [box[2], box[3]],
      ],
      {
        padding: editingPadding,
      },
    );
  }, [editingGeometry, map]);

  useEffect(() => {
    /*Ya que las lineas y poligonos no incluyen su propio onClick, se debe manejar el
    onCLick del componente padre, y para cada layer id incluir un string unico para
    diferenciarlos y poder incluir su propio onCLick
    */
    setLayerIds([
      ...layers.map((l) => 'raster-layer-' + l.name),
      ...lines.map((l) => 'line_click_area-' + l.id),
      ...syncLines.map((l) => 'sync_line_click_area-' + l.id),
      ...polygons.map((l) => 'polygon_fill-' + l.id),
      ...syncPolygons.map((l) => 'sync_polygon_fill-' + l.id),
      ...(addingGeometry && addingGeometryType !== 'punto'
        ? ['map-current-line-clickable']
        : []),
      ...(aoi && aoiVisible ? ['aoi_click_area'] : []),
      'cm_multiple_geometry-polygons',
      'cm_multiple_geometry-lines',
      'cm_multiple_geometry-points',
      'cm_geometry-polygons',
      'cm_geometry-lines',
      'cm_geometry-points'
    ]);
  }, [lines, polygons, syncLines, syncPolygons, addingGeometry, addingGeometryType, layers, taskFeatures, aoi, aoiVisible]);

  useEffect(() => {
    centerMapOnGeometries(map, allGeometries)
  }, [allGeometries]);

  const centerOnLine = useCallback(
    (line: Line | Polygon) => {
      if (!line) return;
      const feature = lineString(
        line.puntos.map((p) => [p.longitud, p.latitud]),
      );
      const box = bbox(feature);
      const normalPadding = { top: 232, left: 300, bottom: 32, right: 300 };
      map?.fitBounds(
        [
          [box[0], box[1]],
          [box[2], box[3]],
        ],
        {
          padding: normalPadding,
        },
      );
    },
    [map],
  );

  const onKeyDown = (e: any) => {
    if (e.key === 'Enter') {
      dispatch(saveCurrentShape());
      e.preventDefault();
    }
    if (e.key === 'Backspace') {
      dispatch(deletePointOfLineSelected());
    }
    if (e.key === 'Escape') {
      dispatch(cancelAddingGeometry());
    }
  };

  const onMouseMove = useMemo(
    () =>
      throttle((e: MapLayerMouseEvent) => {
        if (e.features && e.features?.[0]?.layer?.id) {
          setHoveringId(parseInt(e.features[0].layer.id.split('-')[1]));
        } else {
          setHoveringId(0);
        }
        setMousePosition({ latitud: e.lngLat.lat, longitud: e.lngLat.lng });
      }, 15),
    [],
  );

  const onDoubleClick = useCallback(
    (e: MapLayerMouseEvent) => {
      if (
        canEditGeometries &&
        e.features &&
        (e?.features?.[0]?.layer?.id?.startsWith('line') ||
          e?.features?.[0]?.layer?.id?.startsWith('polygon'))
      ) {
        const [layerType, id] = e.features[0].layer.id.split('-');
        dispatch(
          editExistingLine({
            id: parseInt(id),
            type: layerType.startsWith('line') ? 'linea' : 'poligono',
          }),
        );
        e.preventDefault();
        e.originalEvent.stopPropagation();
      }
    },
    [dispatch, canEditGeometries],
  );

  const onClick = (e: MapLayerMouseEvent) => {
    dispatch(setAOISelected(false));
    dispatch(unselectFeature());
    dispatch(unselectGeometry());

    if(addingAOI){
      dispatch(
        addAOIPoint({
          coord: {
            latitud: e.lngLat.lat,
            longitud: e.lngLat.lng,
          },
          layers: layers
        })
      );
      dispatch(saveAOI())
      return;
    }

    if (addingGeometry && addingGeometryType === 'punto') {
      dispatch(
        addPoint({
          latitud: e.lngLat.lat,
          longitud: e.lngLat.lng,
        }),
      );
      return;
    }
    if (
      addingGeometry &&
      (addingGeometryType === 'linea' || addingGeometryType === 'poligono')
    ) {
      dispatch(
        addPointToLine({
          latitud: e.lngLat.lat,
          longitud: e.lngLat.lng,
        }),
      );
      return;
    }

    if(addingMultipleFeatures && !addingMultipleFeaturesSecondPoint){
      dispatch(
        addMultiplesFeaturesPoint({
          coords: {
            latitud: e.lngLat.lat,
            longitud: e.lngLat.lng,
          },
        })
      );
      return;
    }

    if (e.features && e?.features?.[0]?.layer?.id?.startsWith('cm_multiple_geometry')) {
      dispatch(unselectPreselectedFeature(e.features[0]?.properties?._featureId));
      return;
    }

    if (e.features && e?.features?.[0]?.layer?.id?.startsWith('line')) {
      dispatch(selectLineId(parseInt(e.features[0].layer.id.split('-')[1])));
      return;
    }

    if (e.features && e?.features?.[0]?.layer?.id?.startsWith('polygon')) {
      dispatch(selectPolygonId(parseInt(e.features[0].layer.id.split('-')[1])));
      return;
    }

    /*Ya que las lineas y poligonos no incluyen su propio onClick, se debe manejar el
    onCLick del componente padre, y para cada layer id incluir un string unico para
    diferenciarlos y poder incluir su propio onCLick
    */
    if (e.features && e?.features?.[0]?.layer?.id?.startsWith('sync_line')) {
      dispatch(selectSyncLineId(parseInt(e.features[0].layer.id.split('-')[1])));
      return;
    }

    if (e.features && e?.features?.[0]?.layer?.id?.startsWith('sync_polygon')) {
      dispatch(selectSyncPolygonId(parseInt(e.features[0].layer.id.split('-')[1])));
      return;
    }

    if (e.features && e?.features?.[0]?.layer?.id?.startsWith('cm_geometry')) {
      dispatch(selectFeature({ featureId: e.features[0]?.properties?._featureId, mode: 'view-attributes' } ));
      return;
    }

    if (layers.length) {
      dispatch(
        showLayerAttributes({
          coords: {
            latitud: e.lngLat.lat,
            longitud: e.lngLat.lng,
          },
          mapBounds: map!.getBounds(),
          mapWidth: map!.getCanvas().clientWidth,
          click: 'left'
        }),
      );
      return;
    }
  };

  const onRightClick = (e: MapLayerMouseEvent) => {
    if(!canEditGeometries){
      return;
    }

    dispatch(setAOISelected(false));

    if (e.features && e?.features?.[0]?.layer?.id?.startsWith('cm_geometry')) {
      dispatch(selectFeature({ featureId: e.features[0]?.properties?._featureId, mode: 'remove' } ));
      return;
    }

    if (e.features && e?.features?.[0]?.layer?.id === 'aoi_click_area') {
      dispatch(setAOISelected(true));
      return;
    }
    dispatch(unselectFeature());

    if (layers.length) {
      dispatch(
        showLayerAttributes({
          coords: {
            latitud: e.lngLat.lat,
            longitud: e.lngLat.lng,
          },
          mapBounds: map!.getBounds(),
          mapWidth: map!.getCanvas().clientWidth,
          click: 'right'
        }),
      );
    }
  };

  const transformRequest = (url: string) => {
    if (url.includes('/adjuntos/')) {
      return {
        url,
        headers: {
          Authorization: 'Bearer ' + localStorage.getItem('accessToken')
        }
      }
    } else if(url.includes('geoserver')){
      return {
        url,
        headers: {
          Authorization: 'Basic ' + btoa(
            process.env.REACT_APP_GEOSERVER_USER + ':' + process.env.REACT_APP_GEOSERVER_PASSWORD
          )
        }
      }
    }
  }


  useEffect(() => {
    if (geometrySelected?.tipo === 'punto')
      centerOnPoint(geometrySelected as Point);
    else if (geometrySelected) centerOnLine(geometrySelected as Line | Polygon);
  }, [geometrySelected, centerOnLine, centerOnPoint]);

  return (
    <Layout
      onKeyDown={(e) => {
        onKeyDown(e);
      }}
    >
      <Navbar showItems={false} itemsCallback={itemsCallback} />
      <Layout className="tp-task-map__layout">
        <div>
          <TaskMapView editing={editing} onOpenChange={(open) => setSidebarOpen(open)} />
          {mapTutorial ? (
            <div className="tp-task-map__overlay">
              <div className="tp-task-map__tutorial-message">
                <InfoCircle />
                <span>
                  Mueve el cursor para navegar en el mapa y haz click izquierdo
                  para agregar un punto.
                </span>
              </div>
              <div className="tp-task-map__button-container">
                <Checkbox
                  checked={dontShow}
                  onChange={() => setDontShow(!dontShow)}
                >
                  No mostrar la próxima vez.
                </Checkbox>
                <Button
                  onClick={() => dispatch(closeMapTutorial(dontShow))}
                  type="primary"
                  shape={'round'}
                >
                  Entendido
                </Button>
              </div>
            </div>
          ) : null}
          <MapVideoTutorial videoUrl={'/video/aoi-tutorial.mp4'} visible={aoiTutorial} onClose={dontShowT => {
            if(dontShowT)
              localStorage.setItem('aoiTutorialShown', 'true');
            dispatch(closeAOITutorial());
          }} />
          <MapVideoTutorial videoUrl={'/video/multiple-selection-tutorial.mp4'} visible={multipleSelectionTutorial} onClose={dontShowT => {
            if(dontShowT)
              localStorage.setItem('multipleFeaturesTutorialShown', 'true');
            dispatch(closeMultipleSelectionTutorial());
          }} />
          <MapVideoTutorial videoUrl={'/video/add-geometries.mp4'} visible={addGeometriesTutorial} onClose={dontShowT => {
            if(dontShowT)
              localStorage.setItem('addGeometriesTutorialShown', 'true');
            dispatch(closeAddGeometriesTutorial());
          }} />
          <Map
            id="map"
            onMouseMove={onMouseMove}
            initialViewState={{
             bounds: INITIAL_BOUNDS as any
            }}
            style={{
              height: 'calc(100vh - 64px)',
            }}
            transformRequest={transformRequest as any}
            interactiveLayerIds={layerIds}
            cursor={
              dragging
                ? 'grabbing'
                : hoveringId
                ? 'pointer'
                : addingGeometry
                ? "url('/crosshair.svg') 10 10, auto"
                : 'pointer'
            }
            onClick={onClick}
            onDblClick={onDoubleClick}
            onContextMenu={onRightClick}
            mapStyle={require('./mapStyle.json')}
          >
            <MapOrderingLayer />
            {layers.map((layer) => (
              <Source
                key={layer.name}
                id={'source' + layer.name}
                type="raster"
                tileSize={256}
                tiles={[getLayerUrl(layer)]}
              >
                <Layer beforeId={'order-raster'}  id={'raster-layer-' + layer.name} type={'raster'} />
              </Source>
            ))}
            {points.map((point) => (
              <MapPointMarker
                onCliked={centerOnPoint}
                key={point.id + point.color}
                point={point}
              />
            ))}
            {lines.map((line) => (
              <MapLineMarker
                hovered={hoveringId === line.id}
                key={line.id + line.color}
                line={line}
              />
            ))}
            {polygons.map((polygon) => (
              <MapPolygonMarker
                hovered={hoveringId === polygon.id}
                key={polygon.id + polygon.color}
                polygon={polygon}
              />
            ))}
            {syncPoints.map((point) => (
              <MapPointMarker
                onCliked={centerOnPoint}
                key={point.id + point.color}
                point={point}
                isSync={true}
              />
            ))}
            {syncLines.map((line) => (
              <MapLineMarker
                hovered={hoveringId === line.id}
                key={line.id + line.color}
                line={line}
                isSync={true}
              />
            ))}
            {syncPolygons.map((polygon) => (
              <MapPolygonMarker
                hovered={hoveringId === polygon.id}
                key={polygon.id + polygon.color}
                polygon={polygon}
                isSync={true}
              />
            ))}
            {/**Componente que muestra los atributos de las geometrias no sincronizadas, si este tiene
             * algun dato en la variable selectGeometrySelected
             */}
            <MapPopup />
            {/**Componente que muestra los atributos de las geometrias sincronizadas, si este tiene
             * algun dato en la variable selectSyncGeometrySelected
             */}
            <SyncTaskPopup />
            <MapCMFeaturePopup />
            <MapCMGeometrySelected />
            <MapCMGeometries />
            <MapCMGeometryRemoved />
            <MapCurrentLine
              onDragChange={(d) => setDragging(d)}
              mousePosition={mousePosition}
            />
            <MapAOI mousePosition={mousePosition} />
            <MapMultipleSelection mousePosition={mousePosition} />
            <MapUserLocation />
            <MapCMMultipleGeometriesSelected />
          </Map>
          {mousePosition &&
            <Tooltip
              placement={'top'}
              title={'Puedes copiar la coordenada haciendo Ctrl + Shift + C'}
            >
              <div className={'tp-task-map__coordinates'}
                style={{ left: sidebarOpen ? 420 : 120, }}
              >
                {degreesToDegreeMinuteSecond(mousePosition.latitud, true)} / {degreesToDegreeMinuteSecond(mousePosition.longitud, false)}
              </div>
            </Tooltip>
          }
          <MapButtons
            onZoomIn={() => map?.zoomIn({ animate: true })}
            onZoomOut={() => map?.zoomOut({ animate: true })}
          />
          <MapAddMultipleGeometriesButton />
          {addingGeometry && addingGeometryType !== 'punto' ? (
            <MapBottomLineControl />
          ) : null}
        </div>
      </Layout>
    </Layout>
  );
}

export default TaskMapTemplate;
